import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';

/**
 * MISA eSign documents — DataCentral v4 `/invoices/:id/<doc>` endpoints.
 * Auth: UMA RPT via `dataCentralApi` (same as all DC v4 data).
 *
 * An invoice has two signable documents:
 *   - payment-request         ("Giấy đề nghị thanh toán")
 *   - reconciliation-statement ("Biên bản đối soát", BBĐS — may embed images)
 *
 * Envelope shapes:
 *   Object: { message, result: T }
 *   Preview: raw HTML (responseType 'text')
 *
 * A document may never have been created for an invoice: the detail endpoint
 * then returns 404/empty. Callers treat a missing `status` as "chưa tạo".
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

export type DocumentType = 'payment-request' | 'reconciliation-statement';

/** Image source used by the reconciliation-statement render/sign flow. */
export type ImageSource = 'auto' | 'upload';

/** eSign status. Absent/empty when the document was never created. */
export type SignedDocumentStatus = 'pending' | 'signed' | 'failed' | 'cancelled';

/** One dispatched mail record on a signed document. */
export interface SignedDocumentMail {
  mail_id: number | string;
  status: string;
  to: string;
  subject: string;
  created_at: string;
  error_message?: string | null;
  sent_by_name?: string | null;
}

/** `GET /invoices/:id/<doc>` — the document detail object. */
export interface SignedDocument {
  status?: SignedDocumentStatus | '' | null;
  file_url?: string | null;
  /** Presigned S3 URL — opens directly (no auth), forces a PDF download. */
  file_download_url?: string | null;
  file_url_expires_at?: string | null;
  signed_at?: string | null;
  requested_at?: string | null;
  requested_by_name?: string | null;
  error_message?: string | null;
  /** Prior cancelled versions, same shape. */
  history?: SignedDocument[] | null;
  mails?: SignedDocumentMail[] | null;
  last_sent_at?: string | null;
}

/** `GET /invoices/:id/<doc>/mail-prefill` result. */
export interface DocumentMailPrefill {
  from?: string;
  from_name?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
}

/** `POST /invoices/:id/<doc>/send-mail` body. */
export interface SendDocumentMailBody {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
}

/** `POST /invoices/:id/<doc>/resign` body. */
export interface ResignDocumentBody {
  reason: string;
  image_source?: ImageSource;
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const esignDocumentKeys = {
  all: ['esign-document'] as const,
  document: (invoiceId: number, docType: DocumentType) =>
    ['esign-document', invoiceId, docType] as const,
  preview: (invoiceId: number, docType: DocumentType, imageSource: ImageSource) =>
    ['esign-document', invoiceId, docType, 'preview', imageSource] as const,
  mailPrefill: (invoiceId: number, docType: DocumentType) =>
    ['esign-document', invoiceId, docType, 'mail-prefill'] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /invoices/:id/<doc>` — document detail. Returns `null` when the document
 * was never created (404/empty), surfaced to the UI as "chưa tạo".
 * Polls every 3s while `status === 'pending'` (MISA signer approval is async).
 * Envelope: { message, result: SignedDocument }
 */
export function useSignedDocument(invoiceId: number, docType: DocumentType) {
  return useQuery({
    queryKey: esignDocumentKeys.document(invoiceId, docType),
    queryFn: async (): Promise<SignedDocument | null> => {
      try {
        const res = await dataCentralApi.get<{ message: string; result: SignedDocument }>(
          `/invoices/${invoiceId}/${docType}`,
        );
        return res.data.result ?? null;
      } catch (err) {
        // 404 → document never created for this invoice.
        if (isNotFound(err)) return null;
        throw err;
      }
    },
    enabled: Boolean(invoiceId),
    // Poll only while the MISA signer has not yet resolved the request.
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 3000 : false),
  });
}

/**
 * `GET /invoices/:id/<doc>/preview.html?image_source=` — rendered HTML of the
 * document as it will be signed. Needs the auth token, so it is fetched via
 * `dataCentralApi` (responseType 'text') and rendered into an `<iframe srcDoc>`
 * — a plain `<iframe src>` would not carry the RPT. Lazy: enable from the tab.
 */
export function useSignedDocumentPreview(
  invoiceId: number,
  docType: DocumentType,
  imageSource: ImageSource = 'auto',
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: esignDocumentKeys.preview(invoiceId, docType, imageSource),
    queryFn: async (): Promise<string> => {
      const res = await dataCentralApi.get<string>(
        `/invoices/${invoiceId}/${docType}/preview.html?image_source=${imageSource}`,
        { responseType: 'text' },
      );
      return res.data;
    },
    enabled: Boolean(invoiceId) && (opts?.enabled ?? false),
    staleTime: Infinity,
  });
}

/**
 * `GET /invoices/:id/<doc>/mail-prefill` — recipients + subject defaults for the
 * send-mail form. Lazy: enable from the send-mail dialog.
 * Envelope: { message, result: DocumentMailPrefill }
 */
export function useDocumentMailPrefill(
  invoiceId: number,
  docType: DocumentType,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: esignDocumentKeys.mailPrefill(invoiceId, docType),
    queryFn: async (): Promise<DocumentMailPrefill> => {
      const res = await dataCentralApi.get<{ message: string; result: DocumentMailPrefill }>(
        `/invoices/${invoiceId}/${docType}/mail-prefill`,
      );
      return res.data.result ?? {};
    },
    enabled: Boolean(invoiceId) && (opts?.enabled ?? false),
    staleTime: Infinity,
  });
}

/**
 * `POST /invoices/:id/<doc>/sign` — submit the document to MISA eSign. Status
 * becomes "pending" until the signer approves in the MISA app.
 * USER must trigger this — never called automatically.
 */
export function useSignDocument(invoiceId: number, docType: DocumentType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body?: { image_source?: ImageSource }): Promise<SignedDocument> => {
      const res = await dataCentralApi.post<{ message: string; result: SignedDocument }>(
        `/invoices/${invoiceId}/${docType}/sign`,
        body ?? {},
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: esignDocumentKeys.document(invoiceId, docType) });
    },
  });
}

/**
 * `POST /invoices/:id/<doc>/resign` — cancel the current signed document and
 * sign the invoice's current data again. Requires a `reason`.
 * USER must trigger this — never called automatically.
 */
export function useResignDocument(invoiceId: number, docType: DocumentType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ResignDocumentBody): Promise<SignedDocument> => {
      const res = await dataCentralApi.post<{ message: string; result: SignedDocument }>(
        `/invoices/${invoiceId}/${docType}/resign`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: esignDocumentKeys.document(invoiceId, docType) });
    },
  });
}

/**
 * `POST /invoices/:id/<doc>/send-mail` — email the signed document.
 * USER must trigger this — never called automatically.
 * Envelope: { message, result }
 */
export function useSendDocumentMail(invoiceId: number, docType: DocumentType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SendDocumentMailBody): Promise<Record<string, unknown>> => {
      const res = await dataCentralApi.post<{ message: string; result: Record<string, unknown> }>(
        `/invoices/${invoiceId}/${docType}/send-mail`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: esignDocumentKeys.document(invoiceId, docType) });
    },
  });
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/** True when an axios error carries a 404 (document never created). */
function isNotFound(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const status = (err as { response?: { status?: number } }).response?.status;
  return status === 404;
}
